/** Summary of a chat or a PDF in the reader's language (on request; may take a few seconds). */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useT, type MsgKey } from '../i18n.ts';
import { Dialog } from './Dialog.tsx';

interface Summary {
  summary: string;
  decisions: string[];
  dates: string[];
  commitments: string[];
  openQuestions: string[];
  truncated: boolean;
  count?: number;
}

export function SummaryDialog({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const t = useT();
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<Summary>('POST', url, undefined, { timeoutMs: 120_000 })
      .then(setData)
      .catch((err) => {
        const code = err instanceof ApiError ? err.code : 'network';
        const known: Record<string, MsgKey> = {
          nothing_to_summarise: 'sumNothing',
          pdf_no_text: 'sumNoText',
          pdf_unreadable: 'sumUnreadable',
          budget_exceeded: 'budgetExceeded',
          summary_unavailable: 'sumUnavailable',
        };
        setError(t(known[code] ?? 'sumFailed'));
      });
  }, [url, t]);

  const copy = () => {
    if (!data) return;
    const sec = (h: string, xs: string[]) => (xs.length ? `\n${h}\n${xs.map((x) => `• ${x}`).join('\n')}\n` : '');
    void navigator.clipboard?.writeText(
      `${title}\n\n${data.summary}\n${sec(t('sumDecisions'), data.decisions)}${sec(t('sumDates'), data.dates)}${sec(t('sumCommitments'), data.commitments)}${sec(t('sumOpen'), data.openQuestions)}`,
    );
  };

  const List = ({ k, xs }: { k: MsgKey; xs: string[] }) =>
    xs.length ? (
      <section>
        <h4>{t(k)}</h4>
        <ul>{xs.map((x, i) => <li key={i}>{x}</li>)}</ul>
      </section>
    ) : null;

  return (
    <Dialog title={title} onClose={onClose}>
      {!data && !error && <p className="muted" role="status"><span className="spinner" aria-hidden="true" /> {t('sumWorking')}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
      {data && (
        <div className="summary stack">
          <p>{data.summary}</p>
          <List k="sumDecisions" xs={data.decisions} />
          <List k="sumDates" xs={data.dates} />
          <List k="sumCommitments" xs={data.commitments} />
          <List k="sumOpen" xs={data.openQuestions} />
          <p className="muted small">{data.truncated ? t('sumTruncated') : ''} {t('sumDisclaimer')}</p>
          <div className="row">
            <button className="btn small" onClick={copy}>{t('copyText')}</button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
